// ============================================================
// منابع کاشی نقشه — همه رایگان و بدون کلید
//
// چرا این فایل هست: قبلاً هر کامپوننت نقشه خودش یک منبع کاشی
// هاردکد داشت. سرویس نشان کلید می‌خواست (و کلیدش داخل باندل
// جاوااسکریپت بود، یعنی برای همه قابل خواندن)، و آدرس
// tile.openstreetmap.ir که جایگزینش بود از کار افتاده است.
//
// اینجا فقط سرویس‌های رایگانِ بی‌کلید می‌آیند، به ترتیب اولویت. اگر
// اولی کاشی نداد، کامپوننت به بعدی می‌رود (TileLayerWithFallback).
//
// CARTO اینجا نیست و نباید برگردد: از جایی به بعد کلید خواست و
// به‌جای کاشی، تصویر «API KEY REQUIRED» می‌فرستد — با کد ۲۰۰، پس
// حتی منطق پشتیبان هم آن را خطا نمی‌بیند. (آزمایش: دو کاشی با
// مختصات متفاوت، md5 یکسان برمی‌گردانند.) Esri هم ۴۰۳ می‌دهد.
//
// حالت تیره با فیلتر CSS ساخته می‌شود، نه با منبع جداگانه: هیچ
// سرویس کاشیِ تیره‌ی بی‌کلیدی باقی نمانده است.
// ============================================================

import { useEffect, useState } from "react";
import L from "leaflet";

export interface TileSource {
    id: string;
    url: string;
    attribution: string;
    maxZoom: number;
    subdomains?: string;
}

/** به ترتیب اولویت. اولی همیشه OSM استاندارد است. */
export const TILE_SOURCES: TileSource[] = [
    {
        id: "osm",
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution: "&copy; OpenStreetMap",
        maxZoom: 19,
        subdomains: "abc",
    },
    {
        // فقط پشتیبان: سبکش توپوگرافی است و کاشی‌هایش سنگین‌تر، ولی
        // رایگان و بی‌کلید است و وقتی OSM در دسترس نباشد بهتر از
        // صفحه‌ی خالی است.
        id: "opentopo",
        url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
        attribution: "&copy; OpenStreetMap &middot; OpenTopoMap",
        maxZoom: 17,
        subdomains: "abc",
    },
];

/** کلاسی که روی کاشی‌ها می‌نشیند تا نقشه تیره شود. */
export const DARK_TILE_CLASS = "havir-tiles-dark";

/** بیشترین زومی که هر منبعی پشتیبانی می‌کند. */
export const MAX_ZOOM = 19;
export const MIN_ZOOM = 5;

/** مرکز پیش‌فرض وقتی هیچ موقعیتی ثبت نشده باشد (تهران). */
export const DEFAULT_CENTER: [number, number] = [35.6892, 51.389];

// ---------- حالت تیره ----------
//
// حالت تیره در این سامانه با data-mode روی <html> اعمال می‌شود
// (thunk لایه‌بندی)، نه با کلاس dark. کد قبلی کلاس را چک می‌کرد و
// برای همین نقشه هیچ‌وقت تیره نمی‌شد.
const readDarkMode = (): boolean =>
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-mode") === "dark";

/** حالت تیره را می‌خواند و با عوض شدنش کامپوننت را دوباره رندر می‌کند. */
export const useIsDarkMode = (): boolean => {
    const [dark, setDark] = useState(readDarkMode);

    useEffect(() => {
        const target = document.documentElement;
        const observer = new MutationObserver(() => setDark(readDarkMode()));
        observer.observe(target, { attributes: true, attributeFilter: ["data-mode"] });
        return () => observer.disconnect();
    }, []);

    return dark;
};

// ---------- پین ----------
//
// به‌جای آیکون پیش‌فرض Leaflet که سه تصویر از CDN می‌گیرد، یک پین
// CSS ای می‌سازیم: هیچ درخواست شبکه‌ای ندارد و در بیلد هم نمی‌شکند
// (آیکون پیش‌فرض Leaflet با باندلرهای مدرن مسیرش گم می‌شود).
const PIN_STYLE_ID = "havir-map-pin-style";

const ensurePinStyle = (): void => {
    if (typeof document === "undefined") return;
    if (document.getElementById(PIN_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = PIN_STYLE_ID;
    style.textContent = `
        .havir-map-pin { position: relative; width: 30px; height: 30px; }
        .havir-map-pin i {
            position: absolute; inset: 9px; border-radius: 9999px;
            background: #ef4444; border: 3px solid #fff;
            box-shadow: 0 2px 10px rgba(0,0,0,.4);
        }
        .havir-map-pin b {
            position: absolute; inset: 0; border-radius: 9999px;
            background: rgba(239,68,68,.35);
            animation: havirMapPulse 1.8s ease-out infinite;
        }
        @keyframes havirMapPulse {
            0%   { transform: scale(.45); opacity: .9; }
            70%  { transform: scale(1.7); opacity: 0; }
            100% { transform: scale(1.7); opacity: 0; }
        }
        .leaflet-container { font-family: inherit; background: #e5e7eb; }
        html[data-mode="dark"] .leaflet-container { background: #1f2937; }

        /* نقشه‌ی تیره بدون سرویس تیره: کاشی روشن وارونه می‌شود.
           hue-rotate رنگ‌ها را سر جای خودشان برمی‌گرداند، وگرنه آب
           نارنجی می‌شد. فقط روی کاشی‌ها اعمال می‌شود، پس پین و
           کنترل‌ها دست‌نخورده می‌مانند (pane جداگانه دارند). */
        .${DARK_TILE_CLASS} {
            filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.9);
        }
    `;
    document.head.appendChild(style);
};

/** پین قرمز تپنده — همان ظاهری که نقشه‌های قبلی داشتند. */
export const pulsePin = (): L.DivIcon => {
    ensurePinStyle();
    return L.divIcon({
        className: "",
        html: '<div class="havir-map-pin"><b></b><i></i></div>',
        iconSize: [30, 30],
        iconAnchor: [15, 15],
    });
};

export const round6 = (value: number): number => Math.round(value * 1e6) / 1e6;
